/** UI + API helpers for utilisation component weights (mileage, time, revenue, load). */

export interface UtilisationComponentWeights {
  mileage: number;
  time: number;
  revenue: number;
  load: number;
}

export interface UtilisationWeightsUi {
  milesEff: number;
  revEff: number;
  timeEff: number;
  loadEff: number;
}

export type UtilisationWeightPreset =
  | 'balanced'
  | 'revenue'
  | 'distance'
  | 'custom'
  | 'es_time';

export const DEFAULT_LOGISTICS_COMPONENT_WEIGHTS: UtilisationComponentWeights = {
  mileage: 25,
  time: 25,
  revenue: 25,
  load: 25,
};

export const DEFAULT_ES_COMPONENT_WEIGHTS: UtilisationComponentWeights = {
  mileage: 50,
  time: 50,
  revenue: 0,
  load: 0,
};

export const UTIL_PRESET_WEIGHTS: Record<
  Exclude<UtilisationWeightPreset, 'custom' | 'es_time'>,
  UtilisationWeightsUi
> = {
  balanced: { milesEff: 25, revEff: 25, timeEff: 25, loadEff: 25 },
  revenue: { milesEff: 10, revEff: 50, timeEff: 20, loadEff: 20 },
  distance: { milesEff: 50, revEff: 10, timeEff: 20, loadEff: 20 },
};

export const UTIL_ES_PRESET_WEIGHTS: Record<
  Exclude<UtilisationWeightPreset, 'revenue' | 'custom'>,
  UtilisationWeightsUi
> = {
  balanced: { milesEff: 50, revEff: 0, timeEff: 50, loadEff: 0 },
  distance: { milesEff: 70, revEff: 0, timeEff: 30, loadEff: 0 },
  es_time: { milesEff: 30, revEff: 0, timeEff: 70, loadEff: 0 },
};

export function defaultComponentWeights(
  emergency: boolean,
): UtilisationComponentWeights {
  return emergency
    ? { ...DEFAULT_ES_COMPONENT_WEIGHTS }
    : { ...DEFAULT_LOGISTICS_COMPONENT_WEIGHTS };
}

export function componentWeightsToUi(
  weights: UtilisationComponentWeights,
  emergency: boolean,
): UtilisationWeightsUi {
  if (emergency) {
    return {
      milesEff: weights.mileage,
      timeEff: weights.time,
      revEff: 0,
      loadEff: 0,
    };
  }
  return {
    milesEff: weights.mileage,
    revEff: weights.revenue,
    timeEff: weights.time,
    loadEff: weights.load,
  };
}

export function uiWeightsToComponent(
  ui: UtilisationWeightsUi,
  emergency: boolean,
): UtilisationComponentWeights {
  if (emergency) {
    return {
      mileage: ui.milesEff,
      time: ui.timeEff,
      revenue: 0,
      load: 0,
    };
  }
  return {
    mileage: ui.milesEff,
    time: ui.timeEff,
    revenue: ui.revEff,
    load: ui.loadEff,
  };
}

export function inferWeightPreset(
  ui: UtilisationWeightsUi,
  emergency: boolean,
): UtilisationWeightPreset {
  const presets = emergency ? UTIL_ES_PRESET_WEIGHTS : UTIL_PRESET_WEIGHTS;
  for (const [id, preset] of Object.entries(presets)) {
    if (
      preset.milesEff === ui.milesEff &&
      preset.timeEff === ui.timeEff &&
      preset.revEff === ui.revEff &&
      preset.loadEff === ui.loadEff
    ) {
      return id as UtilisationWeightPreset;
    }
  }
  return 'custom';
}

export function weightsTotal(ui: UtilisationWeightsUi, emergency: boolean): number {
  return emergency
    ? ui.milesEff + ui.timeEff
    : ui.milesEff + ui.revEff + ui.timeEff + ui.loadEff;
}
